import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../lib/api";
import { normalizeSubject } from "../../lib/messages";
import { toast } from "sonner";
import { Mail, Trash2, Reply, ArrowLeft, RefreshCcw, ChevronRight, ChevronDown } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

function MessageRow({ m, active, onSelect, indented = false, showSubject = true }) {
  const unread = m.status === "unread";
  return (
    <button
      onClick={() => onSelect(m)}
      data-testid={`message-row-${m.id}`}
      className={`w-full text-left px-4 py-3 border-b border-berulia-border transition ${active ? "bg-berulia-rose" : "hover:bg-berulia-beigewarm"} ${indented ? "pl-8 bg-berulia-beigewarm/30" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`text-sm truncate ${unread ? "font-semibold text-berulia-ink" : "text-berulia-inkmuted"}`}>
          {m.from_name || m.from_email || "(inconnu)"}
        </span>
        <span className="text-xs text-berulia-inkmuted shrink-0">{fmtDate(m.received_at)}</span>
      </div>
      {showSubject && (
        <div className={`text-sm truncate mt-1 ${unread ? "text-berulia-ink" : "text-berulia-inkmuted"}`}>
          {m.subject || "(sans objet)"}
        </div>
      )}
      <div className="text-xs text-berulia-inkmuted truncate mt-1">{m.preview}</div>
      {unread && <span className="inline-block mt-2 text-[10px] tracking-widest uppercase text-berulia-rosedeep">Non lu</span>}
    </button>
  );
}

function MessageGroup({ group, activeId, onSelect }) {
  const [open, setOpen] = useState(false);
  const [latest, ...rest] = group.messages; // sorted newest-first by caller
  const unreadInGroup = group.messages.filter(m => m.status === "unread").length;
  const total = group.messages.length;
  const subject = latest.subject || "(sans objet)";

  if (total === 1) {
    // No grouping affordance needed for a single-message conversation.
    return <MessageRow m={latest} active={activeId === latest.id} onSelect={onSelect}/>;
  }

  return (
    <div className="border-b border-berulia-border">
      <div className="flex items-stretch">
        <button
          onClick={() => setOpen(o => !o)}
          aria-label={open ? "Replier le fil" : "Déplier le fil"}
          data-testid={`group-toggle-${group.key}`}
          className="px-2 text-berulia-inkmuted hover:text-berulia-ink hover:bg-berulia-beigewarm border-r border-berulia-border flex items-center"
        >
          {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
        </button>
        <button
          onClick={() => onSelect(latest)}
          data-testid={`group-row-${group.key}`}
          className={`flex-1 text-left px-4 py-3 transition ${activeId === latest.id ? "bg-berulia-rose" : "hover:bg-berulia-beigewarm"}`}
        >
          <div className="flex items-center justify-between gap-2">
            <span className={`text-sm truncate ${unreadInGroup > 0 ? "font-semibold text-berulia-ink" : "text-berulia-inkmuted"}`}>
              {latest.from_name || latest.from_email || "(inconnu)"}
              {total > 1 && (
                <span className="ml-2 text-[10px] tracking-widest uppercase text-berulia-rosedeep">
                  {total} messages
                </span>
              )}
            </span>
            <span className="text-xs text-berulia-inkmuted shrink-0">{fmtDate(latest.received_at)}</span>
          </div>
          <div className={`text-sm truncate mt-1 ${unreadInGroup > 0 ? "text-berulia-ink" : "text-berulia-inkmuted"}`}>
            {subject}
          </div>
          <div className="text-xs text-berulia-inkmuted truncate mt-1">{latest.preview}</div>
          {unreadInGroup > 0 && (
            <span className="inline-block mt-2 text-[10px] tracking-widest uppercase text-berulia-rosedeep">
              {unreadInGroup} non lu{unreadInGroup > 1 ? "s" : ""}
            </span>
          )}
        </button>
      </div>
      {open && rest.map((m) => (
        <MessageRow
          key={m.id}
          m={m}
          active={activeId === m.id}
          onSelect={onSelect}
          indented
          showSubject={false}
        />
      ))}
    </div>
  );
}

function ReplyComposer({ original, onSent, onCancel }) {
  const defaultSubject = useMemo(() => {
    const s = original?.subject || "";
    return s.toLowerCase().startsWith("re:") ? s : `Re: ${s}`;
  }, [original]);

  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const formRef = useRef(null);

  // On mobile the composer renders below a potentially long email body, so
  // bring it into view as soon as it opens.
  useEffect(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      // Wrap the plain-text body in a minimal HTML envelope so the customer's
      // MUA still gets a properly-rendered message.
      const html = `<div style="font-family:Georgia,serif;color:#2C2623;line-height:1.55">${
        body.split("\n").map(l => l ? `<p style="margin:0 0 12px">${l.replace(/</g, "&lt;")}</p>` : "<br/>").join("")
      }</div>`;
      await api.post(`/admin/messages/${original.id}/reply`, {
        subject,
        body_html: html,
        body_text: body,
      });
      toast.success("Réponse envoyée ✿");
      onSent();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "L'envoi a échoué");
    } finally {
      setSending(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={send} className="border-t border-berulia-border bg-berulia-beigewarm p-4 sm:p-6 space-y-3">
      <div className="flex items-center justify-between">
        <p className="overline text-berulia-rosedeep">Répondre à {original.from_email}</p>
        <button type="button" onClick={onCancel} className="text-xs text-berulia-inkmuted hover:text-berulia-ink">Annuler</button>
      </div>
      <label className="block">
        <span className="overline">Objet</span>
        <input
          required value={subject} onChange={e=>setSubject(e.target.value)}
          data-testid="reply-subject"
          className="mt-1 w-full border border-berulia-border bg-white px-3 py-2 text-sm"
        />
      </label>
      <label className="block">
        <span className="overline">Message</span>
        <textarea
          required value={body} onChange={e=>setBody(e.target.value)}
          rows={10}
          data-testid="reply-body"
          placeholder="Bonjour,&#10;&#10;Merci pour votre message…"
          className="mt-1 w-full border border-berulia-border bg-white px-3 py-2 text-sm"
        />
      </label>
      <button
        type="submit" disabled={sending || !body.trim()}
        data-testid="reply-send"
        className="bg-berulia-ink text-berulia-beige overline px-6 py-3 disabled:opacity-50"
      >
        {sending ? "Envoi…" : "Envoyer la réponse →"}
      </button>
    </form>
  );
}

function ThreadView({ thread }) {
  if (!thread?.length) return null;
  return (
    <div className="px-6 py-4 border-t border-berulia-border space-y-3 bg-berulia-beigewarm/50">
      <p className="overline text-berulia-inkmuted">Fil de discussion · {thread.length} message{thread.length > 1 ? "s" : ""}</p>
      {thread.map((t) => (
        <div key={t.id} className="text-xs text-berulia-inkmuted">
          <span className={t.direction === "outbound" ? "text-berulia-rosedeep" : ""}>
            {t.direction === "outbound" ? "Vous → " : ""}{t.from_email}
          </span>
          {" · "}{fmtDate(t.received_at)}
          {" · "}<span className="text-berulia-ink">{t.preview}</span>
        </div>
      ))}
    </div>
  );
}

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [composing, setComposing] = useState(false);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const loadList = async () => {
    setLoadingList(true);
    try {
      const r = await api.get("/admin/messages");
      setMessages(r.data || []);
    } catch {
      toast.error("Impossible de charger les messages");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => { loadList(); }, []);

  const openMessage = async (m) => {
    setSelected(m);
    setComposing(false);
    setLoadingDetail(true);
    try {
      const r = await api.get(`/admin/messages/${m.id}`);
      setDetail(r.data);
      // Optimistically flip status on the list-side so the unread badge clears.
      setMessages((prev) => prev.map(x => x.id === m.id ? { ...x, status: "read" } : x));
    } catch {
      toast.error("Impossible d'ouvrir le message");
    } finally {
      setLoadingDetail(false);
    }
  };

  const onDelete = async () => {
    if (!selected) return;
    if (!window.confirm("Supprimer définitivement ce message ?")) return;
    try {
      await api.delete(`/admin/messages/${selected.id}`);
      toast.success("Message supprimé");
      setMessages((prev) => prev.filter(x => x.id !== selected.id));
      setSelected(null);
      setDetail(null);
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const onReplySent = async () => {
    setComposing(false);
    await loadList();
    if (selected) {
      const r = await api.get(`/admin/messages/${selected.id}`);
      setDetail(r.data);
    }
  };

  const unreadCount = messages.filter(m => m.status === "unread").length;
  const msg = detail?.message;

  // Group by normalized subject. Messages arrive newest-first from the API
  // and we keep that order both inside each group and across groups (group
  // sort key = latest received_at in the group).
  const groups = useMemo(() => {
    const map = new Map();
    for (const m of messages) {
      const key = normalizeSubject(m.subject) || "(sans objet)";
      if (!map.has(key)) map.set(key, { key, messages: [] });
      map.get(key).messages.push(m);
    }
    return Array.from(map.values()).sort(
      (a, b) => (b.messages[0]?.received_at || "").localeCompare(a.messages[0]?.received_at || "")
    );
  }, [messages]);

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="overline text-berulia-rosedeep">BERULIA</p>
          <h1 className="font-heading text-3xl sm:text-4xl mt-2">Messages</h1>
          <p className="text-sm text-berulia-inkmuted mt-1">
            Boîte de réception <strong>contact@berulia.fr</strong> · {unreadCount} non lu{unreadCount > 1 ? "s" : ""}
          </p>
        </div>
        <button
          onClick={loadList} disabled={loadingList}
          data-testid="messages-refresh"
          className="flex items-center gap-2 border border-berulia-border bg-white px-3 py-2 text-sm hover:bg-berulia-beigewarm"
        >
          <RefreshCcw size={14} className={loadingList ? "animate-spin" : ""}/>
          Rafraîchir
        </button>
      </div>

      <div className="grid lg:grid-cols-[360px,1fr] gap-0 border border-berulia-border bg-white min-h-[60vh]">
        {/* List */}
        <div className={`border-r border-berulia-border overflow-y-auto ${selected ? "hidden lg:block" : ""}`}>
          {messages.length === 0 && !loadingList && (
            <div className="p-8 text-center text-sm text-berulia-inkmuted">
              <Mail className="mx-auto mb-3 opacity-50" size={28}/>
              Aucun message pour le moment.
            </div>
          )}
          {groups.map((g) => (
            <MessageGroup key={g.key} group={g} activeId={selected?.id} onSelect={openMessage}/>
          ))}
        </div>

        {/* Detail */}
        <div className={`flex flex-col ${selected ? "" : "hidden lg:flex"}`}>
          {!msg && !loadingDetail && (
            <div className="flex-1 flex items-center justify-center text-sm text-berulia-inkmuted p-8">
              <span>Sélectionnez un message pour le consulter.</span>
            </div>
          )}
          {loadingDetail && <div className="p-8 text-sm text-berulia-inkmuted">Chargement…</div>}
          {msg && (
            <>
              <div className="px-4 sm:px-6 py-4 border-b border-berulia-border flex flex-wrap items-center justify-between gap-2 sm:gap-3">
                <button
                  onClick={() => { setSelected(null); setDetail(null); setComposing(false); }}
                  className="lg:hidden text-sm text-berulia-inkmuted flex items-center gap-1 order-1"
                >
                  <ArrowLeft size={14}/> Retour
                </button>
                <div className="min-w-0 order-3 w-full sm:order-2 sm:w-auto sm:flex-1">
                  <div className="font-heading text-xl sm:text-2xl break-words">{msg.subject || "(sans objet)"}</div>
                  <div className="text-sm text-berulia-inkmuted mt-1 break-words">
                    De <strong>{msg.from_name || msg.from_email}</strong>
                    {msg.from_name && <span> &lt;{msg.from_email}&gt;</span>}
                    {" · "}{fmtDate(msg.received_at)}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0 order-2 sm:order-3">
                  {!composing && (
                    <button
                      onClick={() => setComposing(true)}
                      data-testid="message-reply"
                      className="bg-berulia-ink text-berulia-beige overline px-4 py-2 flex items-center gap-2"
                    >
                      <Reply size={14}/> Répondre
                    </button>
                  )}
                  <button
                    onClick={onDelete}
                    data-testid="message-delete"
                    className="border border-berulia-border px-3 py-2 text-sm text-berulia-inkmuted hover:text-red-700"
                    title="Supprimer"
                  >
                    <Trash2 size={14}/>
                  </button>
                </div>
              </div>

              <div className="p-4 sm:p-6 overflow-y-auto overflow-x-auto flex-1">
                {msg.body_html ? (
                  <div
                    className="prose prose-sm max-w-none break-words [&_img]:max-w-full [&_img]:h-auto [&_table]:!w-full [&_table]:!max-w-full [&_*]:!max-w-full"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: msg.body_html }}
                  />
                ) : (
                  <pre className="whitespace-pre-wrap break-words font-sans text-sm text-berulia-ink">{msg.body_text}</pre>
                )}
              </div>

              <ThreadView thread={(detail?.thread || []).filter(t => t.id !== msg.id)}/>
              {composing && (
                <ReplyComposer original={msg} onSent={onReplySent} onCancel={() => setComposing(false)}/>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
